import { RefObject, useLayoutEffect, useState } from 'react';

export interface ElementSize {
  width: number;
  height: number;
}

/**
 * Tracks an element's rendered size via ResizeObserver. `deps` forces a re-observe when
 * `ref.current` points at a brand new DOM node (e.g. the element unmounted/remounted).
 */
export function useElementSize<T extends HTMLElement>(ref: RefObject<T | null>, deps: unknown[] = []): ElementSize {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, deps);

  return size;
}
